import {
  APIGetKongzResponse,
  BooleanTraitType,
  KollectionSortType,
  KollectionType,
  StringTraitsType,
  StringTraitType,
} from '@cyberkongz/api-interfaces';
import { useInfiniteQuery } from '@tanstack/react-query';
import { NextRouter, useRouter } from 'next/router';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useAccount } from 'wagmi';

import { useApiBaseUrl } from '../../hooks';
import { fetchHttp } from '../../utils';

type ModalType = 'filters' | 'sort';

type NetworkType = 'ethereum' | 'ronin';
type StageType = '0' | '1';
type StatusType = 'locked' | 'unlocked';

type GenkaiFiltersType = {
  network?: NetworkType;
  stage?: StageType;
  status?: StatusType;
};

export type StringTraitsWithCountType = {
  traitType: string;
  values: { value: string; count: number }[];
};

interface MutableKollectionsState {
  kollection: KollectionType;
  sort: KollectionSortType;
  stringTraits: StringTraitsType[];
  booleanTraits: {
    [key in BooleanTraitType]: boolean;
  };
  modals: { [key in ModalType]: boolean };
  query?: ReturnType<typeof useInfiniteQuery<APIGetKongzResponse>>;
  // TO DO find nicer way for kollection specific traits naming
  // genkai specific
  genkaiFilters: GenkaiFiltersType;
}

type GenkaiFilterType =
  | { filter: 'network'; value?: NetworkType }
  | { filter: 'stage'; value?: StageType }
  | { filter: 'status'; value?: StatusType };

interface KollectionsFunctions {
  setKollection: (kollection: KollectionType) => void;
  setSort: (sort: KollectionSortType) => void;
  setStringTrait: (stringTrait: StringTraitType) => void;
  setBooleanTrait: (booleanTrait: BooleanTraitType) => void;
  toggleModal: (modalType: ModalType) => void;
  clearFilters: () => void;
  setGenkaiFilter: (genkaiFilter: GenkaiFilterType) => void;
}

export type KollectionsAction =
  | { type: 'SET_KOLLECTION'; payload: KollectionType }
  | { type: 'SET_SORT'; payload: KollectionSortType }
  | { type: 'SET_STRING_TRAIT'; payload: StringTraitType }
  | { type: 'SET_BOOLEAN_TRAIT'; payload: BooleanTraitType }
  | { type: 'TOGGLE_MODAL'; payload: ModalType }
  | { type: 'CLEAR_FILTERS' }
  | {
      type: 'SET_GENKAI_FILTER';
      payload: GenkaiFilterType;
    };

export type KollectionsContextState = MutableKollectionsState &
  KollectionsFunctions & {
    kollectionStringTraits: StringTraitsWithCountType[];
  };

const KollectionsContext = createContext<KollectionsContextState>({} as KollectionsContextState);

const INITIAL_FILTERS: Pick<
  MutableKollectionsState,
  'stringTraits' | 'genkaiFilters' | 'booleanTraits'
> = {
  stringTraits: [],
  booleanTraits: {
    legendary: false,
  },
  genkaiFilters: {},
};

function INITIAL_STATE(initialKollection: KollectionType): MutableKollectionsState {
  return {
    kollection: initialKollection,
    sort: 'id_asc',
    modals: {
      filters: false,
      sort: false,
    },
    ...INITIAL_FILTERS,
  };
}

function kollectionsReducer(
  state: MutableKollectionsState,
  action: KollectionsAction
): MutableKollectionsState {
  switch (action.type) {
    case 'SET_KOLLECTION': {
      return { ...state, ...INITIAL_FILTERS, kollection: action.payload };
    }
    case 'SET_SORT': {
      return { ...state, sort: action.payload };
    }
    case 'SET_STRING_TRAIT': {
      const { stringTraits } = state;
      const {
        payload: { traitType, value },
      } = action;
      const traitIndex = stringTraits.findIndex((el) => el.traitType === traitType);

      if (traitIndex > -1) {
        const { values } = stringTraits[traitIndex];
        const valueIndex = values.findIndex((el) => el === value);

        // filter out or add new value to values
        const newValues =
          valueIndex > -1 ? values.filter((el) => el !== value) : [...values, value];

        // update trait type at given index with new values
        const newStringTraits = stringTraits
          .map((el, i) => (i === traitIndex ? { traitType, values: newValues } : el))
          .filter((el) => el.values.length !== 0);

        return { ...state, stringTraits: newStringTraits };
      } else {
        // add new trait type with values
        return { ...state, stringTraits: [...stringTraits, { traitType, values: [value] }] };
      }
    }
    case 'SET_BOOLEAN_TRAIT': {
      const { booleanTraits } = state;
      const { payload: booleanTrait } = action;
      return {
        ...state,
        booleanTraits: {
          ...booleanTraits,
          [booleanTrait]: !booleanTraits[booleanTrait],
        },
      };
    }
    case 'TOGGLE_MODAL': {
      return {
        ...state,
        modals: {
          ...state.modals,
          [action.payload]: !state.modals[action.payload],
        },
      };
    }
    case 'SET_GENKAI_FILTER': {
      return {
        ...state,
        genkaiFilters: {
          ...state.genkaiFilters,
          [action.payload.filter]: action.payload.value,
        },
      };
    }
    case 'CLEAR_FILTERS': {
      return {
        ...state,
        ...INITIAL_FILTERS,
      };
    }
    default: {
      throw new Error(`Unhandled action: ${JSON.stringify(action)}`);
    }
  }
}

function getInitialParams(
  state: MutableKollectionsState,
  router: NextRouter
): MutableKollectionsState {
  const queryParamsStr = router.asPath.split('?').slice(1).join('');
  const urlSearchParams = new URLSearchParams(queryParamsStr);
  const params = Object.fromEntries(urlSearchParams.entries());

  try {
    return {
      ...state,
      ...params,
      ...(params.stringTraits ? { stringTraits: [...JSON.parse(params.stringTraits)] } : {}),
      genkaiFilters: {
        network: (params.network as NetworkType) || undefined,
        stage: (params.stage as StageType) || undefined,
        status: (params.status as StatusType) || undefined,
      },
    };
  } catch (err) {
    console.error(`Error while parsing initial query params ${err}`);
    return state;
  }
}

function getAPIQueryParams({
  address,
  kollection,
  sort,
  stringTraits,
  booleanTraits,
  genkaiFilters,
}: {
  address?: `0x${string}`;
  kollection: KollectionType;
  sort: KollectionSortType;
  stringTraits: StringTraitsType[];
  booleanTraits: {
    [key in BooleanTraitType]: boolean;
  };
  genkaiFilters: GenkaiFiltersType;
}) {
  const booleanTraitsQueryParam = Object.entries(booleanTraits).reduce((query, [trait, value]) => {
    if (value) {
      return `${query}&${trait}=${value}`;
    }

    return query;
  }, '');

  let query = `?kollection=${kollection}&sort=${sort}${booleanTraitsQueryParam}`;

  if (stringTraits && stringTraits.length > 0) {
    query += `&stringTraits=${encodeURIComponent(JSON.stringify(stringTraits))}`;
  }

  if (address) {
    query += `&address=${address}`;
  }

  const { network, stage, status } = genkaiFilters;

  if (network) {
    query += `&network=${network}`;
  }

  if (stage) {
    query += `&stage=${stage}`;
  }

  if (status) {
    query += `&status=${status}`;
  }

  return query;
}

export function KollectionsProvider({
  children,
  useConnectedAddress,
  initialKollection,
  kollectionsStringTraits,
}: PropsWithChildren & {
  useConnectedAddress?: boolean;
  initialKollection?: KollectionType;
  kollectionsStringTraits: { [key in KollectionType]: StringTraitsWithCountType[] };
}) {
  const router = useRouter();
  const { address } = useAccount();
  const { API_BASE_URL } = useApiBaseUrl();

  const connectedAddress = useConnectedAddress ? address : undefined;
  const [state, dispatch] = useReducer(
    kollectionsReducer,
    INITIAL_STATE(initialKollection || 'genesis'),
    (state) => getInitialParams(state, router)
  );
  const { kollection, sort, stringTraits, booleanTraits, genkaiFilters } = state;
  const queryParams = useMemo(
    () =>
      getAPIQueryParams({
        address: connectedAddress,
        kollection,
        sort,
        stringTraits,
        booleanTraits,
        genkaiFilters,
      }),
    [connectedAddress, kollection, sort, stringTraits, booleanTraits, genkaiFilters]
  );
  // helper for preventing unneded refetches in background
  const [infiniteQueryEnabled, setInfiniteQueryEnabled] = useState(true);

  const query = useInfiniteQuery<APIGetKongzResponse>({
    queryKey: [
      '/api/kongz',
      { connectedAddress, kollection, sort, stringTraits, booleanTraits, genkaiFilters },
    ],
    queryFn: ({ pageParam }) =>
      fetchHttp(`${API_BASE_URL}/kongz${queryParams}${pageParam ? `&cursor=${pageParam}` : ''}`),
    retry: false,
    refetchInterval: 0,
    initialPageParam: 0,
    gcTime: 0,
    getNextPageParam: (lastPage) => lastPage.endCursor,
    enabled: infiniteQueryEnabled && useConnectedAddress ? !!connectedAddress : true,
  });

  useEffect(() => {
    router.replace(`${window.location.pathname}/${queryParams}`);
  }, [queryParams]);

  useEffect(() => {
    if (query.hasNextPage === false) {
      setInfiniteQueryEnabled(false);
    } else {
      setInfiniteQueryEnabled(true);
    }
  }, [query.hasNextPage]);

  function setKollection(kollection: KollectionType) {
    dispatch({ type: 'SET_KOLLECTION', payload: kollection });
  }

  function setSort(sort: KollectionSortType) {
    dispatch({ type: 'SET_SORT', payload: sort });
  }

  function setStringTrait(stringTrait: StringTraitType) {
    dispatch({ type: 'SET_STRING_TRAIT', payload: stringTrait });
  }

  function setBooleanTrait(booleanTrait: BooleanTraitType) {
    dispatch({ type: 'SET_BOOLEAN_TRAIT', payload: booleanTrait });
  }

  function toggleModal(modalType: ModalType) {
    dispatch({ type: 'TOGGLE_MODAL', payload: modalType });
  }

  function clearFilters() {
    dispatch({ type: 'CLEAR_FILTERS' });
  }

  function setGenkaiFilter(genkaiFilter: GenkaiFilterType) {
    dispatch({ type: 'SET_GENKAI_FILTER', payload: genkaiFilter });
  }

  const kollectionStringTraits = useMemo(() => kollectionsStringTraits[kollection], [kollection]);

  return (
    <KollectionsContext.Provider
      value={{
        ...state,
        setKollection,
        setSort,
        setStringTrait,
        setBooleanTrait,
        toggleModal,
        clearFilters,
        setGenkaiFilter,
        query,
        kollectionStringTraits,
      }}
    >
      {children}
    </KollectionsContext.Provider>
  );
}

export function useKollections() {
  const context = useContext(KollectionsContext);

  if (context === undefined) {
    throw new Error('useKollections must be used within a KollectionsProvider');
  }

  return context;
}
